import React, { useState, useMemo, useEffect } from "react";

import PageContainer from "../../components/common/PageContainer";
import PropertyCard from "../../components/properties/PropertyCard";
import PropertySummaryCard, {
  getTitle,
  getLocation,
  getPrice,
} from "../../components/properties/PropertySummaryCard";
import PropertiesFilters from "../../components/properties/PropertiesFilters";
import PropertyFormModal from "../../components/properties/PropertyFormModal";
import BaseTable from "../../components/table/BaseTable";
import Modal from "../../components/ui/Modal";
import Image from "../../components/ui/Image";
import Loader from "../../components/ui/Loader/Loader";
import Pagination from "../../components/common/pagination/Pagination";

import {
  usePropertiesQuery,
  usePropertiesByAgentQuery,
} from "../../queries/properties/usePropertiesQuery";
import { useAgentsOptionsQuery } from "../../queries/agents/useAgentsQuery";
import usePropertiesFilter from "../../hooks/usePropertiesFilter";
import useIsMobile from "../../hooks/useIsMobile";
import { usePropertyStore } from "../../store/propertyStore";
import { useAuthStore } from "../../store/authStore";

import "../../styles/properties.css";

const PAGE_SIZE_OPTIONS = [2, 5, 10, 25, 50, 100];

function normalizeList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.properties)) return data.properties;
  return [];
}

function getAgentName(property, agentNameById) {
  const agentId = String(property?.agent_id ?? property?.agentId ?? "");
  return (
    property?.agent_name ||
    property?.agent?.name ||
    agentNameById[agentId] ||
    "—"
  );
}

export default function AllProperties() {
  const isMobile = useIsMobile();
  const { role, user } = useAuthStore();
  const isAdmin = role === "admin";
  const isAgent = role === "agent";
  const { selectedProperty } = usePropertyStore();
  const { data: allPropertiesData, isLoading: isAllLoading } =
    usePropertiesQuery({ enabled: isAdmin });
  const { data: agentPropertiesData, isLoading: isAgentLoading } =
    usePropertiesByAgentQuery(isAgent ? user?.id : null);
  const { data: agentsOptions } = useAgentsOptionsQuery({
    enabled: isAdmin,
  });
  const propertiesData = isAdmin ? allPropertiesData : agentPropertiesData;
  const isLoading = isAdmin ? isAllLoading : isAgentLoading;

  const [filterAgent, setFilterAgent] = useState("All");
  const [filterArea, setFilterArea] = useState("All");
  const [filterCountry, setFilterCountry] = useState("All");
  const [minPrice, setMinPrice] = useState("");
  const [minPriceUnit, setMinPriceUnit] = useState("L");
  const [maxPrice, setMaxPrice] = useState("");
  const [maxPriceUnit, setMaxPriceUnit] = useState("L");
  const [viewProperty, setViewProperty] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const properties = useMemo(
    () => normalizeList(propertiesData),
    [propertiesData],
  );

  const agentOptions = useMemo(
    () =>
      normalizeList(agentsOptions).map((agent) => ({
        label: agent.name || agent.label,
        value: String(agent.id ?? agent.value),
      })),
    [agentsOptions],
  );

  const agentNameById = useMemo(
    () =>
      Object.fromEntries(
        agentOptions.map((agent) => [agent.value, agent.label]),
      ),
    [agentOptions],
  );

  const filteredProperties = usePropertiesFilter(
    properties,
    filterAgent,
    filterArea,
    filterCountry,
    minPrice,
    minPriceUnit,
    maxPrice,
    maxPriceUnit,
  );

  useEffect(() => {
    if (selectedProperty) setViewProperty(null);
  }, [selectedProperty]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    filterAgent,
    filterArea,
    filterCountry,
    minPrice,
    minPriceUnit,
    maxPrice,
    maxPriceUnit,
    pageSize,
  ]);

  const totalProperties = filteredProperties.length;
  const totalPages = Math.max(1, Math.ceil(totalProperties / pageSize));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);
  const safePage = Math.min(currentPage, totalPages);

  const paginatedProperties = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return filteredProperties.slice(start, start + pageSize);
  }, [filteredProperties, safePage, pageSize]);

  const showingFrom = totalProperties === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const showingTo = Math.min(safePage * pageSize, totalProperties);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
  };

  const handleResetFilters = () => {
    setFilterAgent("All");
    setFilterArea("All");
    setFilterCountry("All");
    setMinPrice("");
    setMinPriceUnit("L");
    setMaxPrice("");
    setMaxPriceUnit("L");
    setCurrentPage(1);
  };

  const columns = useMemo(
    () => [
      {
        title: "Property",
        dataIndex: "name",
        render: (_, row) => (
          <div className="d-flex align-center gap-2">
            <Image
              src={typeof row.image_url === "string" ? row.image_url : null}
              alt={getTitle(row)}
              width={40}
              height={40}
              className="property-summary-thumb"
            />
            <span className="fw-medium text-primary">{getTitle(row)}</span>
          </div>
        ),
      },
      ...(isAdmin
        ? [
            {
              title: "Agent",
              dataIndex: "agent_id",
              render: (_, row) => getAgentName(row, agentNameById),
            },
          ]
        : []),
      {
        title: "Location",
        dataIndex: "area",
        render: (_, row) => getLocation(row),
      },
      {
        title: "Country",
        dataIndex: "country",
        render: (country) => country || "—",
      },
      {
        title: "Price",
        dataIndex: "min_budget",
        render: (_, row) => (
          <span className="text-primary">{getPrice(row)}</span>
        ),
      },
      {
        title: "Status",
        dataIndex: "is_active",
        render: (isActive) => (
          <span className={`status-badge ${isActive ? "active" : "inactive"}`}>
            {isActive ? "Active" : "Inactive"}
          </span>
        ),
      },
    ],
    [agentNameById, isAdmin],
  );

  if (isLoading) return <Loader />;

  return (
    <PageContainer>
      <div className="d-flex justify-between align-center mb-3">
        <h2 className="text-primary m-0">
          {isAdmin ? "All Properties" : "Properties"}
        </h2>
      </div>

      <PropertiesFilters
        filterAgent={filterAgent}
        setFilterAgent={setFilterAgent}
        filterArea={filterArea}
        setFilterArea={setFilterArea}
        filterCountry={filterCountry}
        setFilterCountry={setFilterCountry}
        minPrice={minPrice}
        setMinPrice={setMinPrice}
        minPriceUnit={minPriceUnit}
        setMinPriceUnit={setMinPriceUnit}
        maxPrice={maxPrice}
        setMaxPrice={setMaxPrice}
        maxPriceUnit={maxPriceUnit}
        setMaxPriceUnit={setMaxPriceUnit}
        agents={agentOptions}
        properties={properties}
        onReset={handleResetFilters}
      />

      <div className="d-flex align-center justify-between mb-2 flex-wrap gap-2">
        {totalProperties > 0 && (
          <div className="text-muted">
            Showing {showingFrom}-{showingTo} of {totalProperties} total
            {totalProperties !== properties.length
              ? ` (filtered from ${properties.length})`
              : ""}
          </div>
        )}

        <div className="d-flex align-center gap-1">
          <label htmlFor="properties-page-size" className="text-muted">
            Per page:
          </label>
          <select
            id="properties-page-size"
            value={pageSize}
            className="filter-select"
            onChange={handlePageSizeChange}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>

      {totalProperties > 0 ? (
        isMobile ? (
          <div className="all-properties-list">
            {paginatedProperties.map((property) => (
              <PropertySummaryCard
                key={property.id}
                property={property}
                agentName={
                  isAdmin ? getAgentName(property, agentNameById) : undefined
                }
                onClick={setViewProperty}
              />
            ))}
          </div>
        ) : (
          <BaseTable
            columns={columns}
            data={paginatedProperties}
            rowKey="id"
            onRowClick={setViewProperty}
          />
        )
      ) : (
        <div className="text-center text-muted" style={{ padding: "24px 0" }}>
          No properties found
        </div>
      )}

      {totalProperties > 0 && totalPages > 1 && (
        <div className="d-flex align-center my-3 justify-center">
          <Pagination
            currentPage={safePage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      <Modal
        isOpen={!!viewProperty}
        onClose={() => setViewProperty(null)}
        title={viewProperty ? getTitle(viewProperty) : "Property Details"}
        className="property-form-modal property-details-modal"
      >
        {viewProperty && <PropertyCard property={viewProperty} />}
      </Modal>

      <PropertyFormModal />
    </PageContainer>
  );
}
